import React, { useState, useEffect } from 'react';
import { contentApi } from '../../services/api';
import Bearbeitungslog from '../logs/Bearbeitungslog';
import MeldungenSection from '../shared/MeldungenSection';
import DeleteReasonModal from '../shared/DeleteReasonModal';

function MarketplaceDetail({ marketplaceObjectId, onBack }) {
  const [object, setObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');

  // Fetch marketplace object details
  const fetchObject = async () => {
    try {
      const response = await contentApi.getPost(marketplaceObjectId);
      setObject(response.data);
    } catch (err) {
      setError('Fehler beim Laden des Marktplatzobjekts');
      console.error('Marketplace object fetch error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchObject();
      setIsLoading(false);
    };
    loadData();
  }, [marketplaceObjectId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isDeleted = () => {
    return object?.deleted_at != null;
  };

  const getStatusBadge = () => {
    if (isDeleted()) {
      return <span className="status-badge suspended">Gelöscht</span>;
    }
    if (object?.sold_at) {
      return <span className="status-badge info">Verkauft</span>;
    }
    return <span className="status-badge verified">Aktiv</span>;
  };

  const getMarketplaceTypeBadge = () => {
    if (object?.marketplace_type === 'offer') {
      return <span className="type-badge marketplace">Ich biete</span>;
    }
    if (object?.marketplace_type === 'search') {
      return <span className="type-badge info">Ich suche</span>;
    }
    return <span className="type-badge">{object?.marketplace_type || '-'}</span>;
  };

  // Action handlers
  // Issue #408: Delete confirmation with documented reason
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    setError('');
    setSuccessMessage('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (reason) => {
    setActionLoading(true);
    try {
      await contentApi.deletePost(marketplaceObjectId, reason);
      setSuccessMessage('Marktplatzobjekt gelöscht');
      setShowDeleteModal(false);
      await fetchObject();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      if (err.response?.data?.error === 'already_deleted') {
        setError('Marktplatzobjekt ist bereits gelöscht');
        setShowDeleteModal(false);
        throw err;
      }
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const openRestoreModal = () => {
    setRestoreReason('');
    setShowRestoreModal(true);
  };

  const closeRestoreModal = () => {
    setShowRestoreModal(false);
    setRestoreReason('');
  };

  const handleRestore = async () => {
    if (!restoreReason.trim()) {
      setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await contentApi.restorePost(marketplaceObjectId, restoreReason.trim());
      setSuccessMessage('Marktplatzobjekt wiederhergestellt');
      closeRestoreModal();
      await fetchObject();
    } catch (err) {
      if (err.response?.data?.error === 'not_deleted') {
        setError('Marktplatzobjekt ist nicht gelöscht');
      } else if (err.response?.data?.error === 'reason_required') {
        setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      } else {
        setError('Wiederherstellen fehlgeschlagen: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!object) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'Marktplatzobjekt nicht gefunden'}</div>
        <button className="secondary-button" onClick={onBack}>Zurück</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header with Back Button */}
      <div className="page-header">
        <div>
          <button className="secondary-button" onClick={onBack} style={{ marginBottom: '12px' }}>
            ← Zurück zur Liste
          </button>
          <h1>Marktplatzobjekt Details</h1>
          <p className="page-subtitle">{object.title || 'Ohne Titel'}</p>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Section 1: Object Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Objektinformationen</h2>
          {getStatusBadge()}
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Titel</label>
            <span>{object.title || '-'}</span>
          </div>
          <div className="info-item">
            <label>Typ</label>
            {getMarketplaceTypeBadge()}
          </div>
          <div className="info-item">
            <label>Preis</label>
            <span>
              {object.is_free_giveaway ? (
                <span className="free-badge">Zu verschenken</span>
              ) : object.price ? (
                `${object.price} €`
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="info-item">
            <label>Erstellt am</label>
            <span>{formatDate(object.created_at)}</span>
          </div>
          <div className="info-item">
            <label>Aktualisiert am</label>
            <span>{formatDate(object.updated_at)}</span>
          </div>
          <div className="info-item">
            <label>Anonym</label>
            <span>{object.is_anonymous ? <span className="anonym-checkmark">✓</span> : '-'}</span>
          </div>
        </div>

        <div className="info-item full-width" style={{ marginTop: '16px' }}>
          <label>Beschreibung</label>
          <div className="content-preview">{object.content}</div>
        </div>
      </section>

      {/* Section 2: Author Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Autor</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <span>{object.is_anonymous ? <em>Anonym</em> : object.author_name}</span>
          </div>
          <div className="info-item">
            <label>E-Mail</label>
            <span>{object.author_email}</span>
          </div>
          <div className="info-item">
            <label>Hochschule</label>
            <span>{object.author_university || '-'}</span>
          </div>
        </div>
      </section>

      {/* Section 3: Engagement Metrics */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Engagement</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Herzen</label>
            <span className="metric-value">{object.hearts_count || 0}</span>
          </div>
          <div className="info-item">
            <label>Klicks</label>
            <span className="metric-value">{object.clicks_count || 0}</span>
          </div>
          <div className="info-item">
            <label>Meldungen</label>
            <span className={`metric-value ${(object.report_count || 0) >= 5 ? 'warning' : ''}`}>
              {object.report_count || 0}
            </span>
          </div>
          <div className="info-item">
            <label>Verkauft am</label>
            <span>{object.sold_at ? formatDate(object.sold_at) : '-'}</span>
          </div>
        </div>
      </section>

      {/* Section 4: Deletion Info (if deleted) */}
      {isDeleted() && (
        <section className="detail-section warning-section">
          <div className="section-header">
            <h2>Löschung</h2>
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label>Gelöscht am</label>
              <span>{formatDate(object.deleted_at)}</span>
            </div>
            <div className="info-item">
              <label>Gelöscht von</label>
              <span>
                {object.deleted_by_creator === true ? 'Ersteller' :
                 object.deleted_by_creator === false ? 'Administrator' :
                 '-'}
              </span>
            </div>
            <div className="info-item">
              <label>Automatisch gelöscht</label>
              <span>
                {object.deleted_by_creator === false ? (
                  <span className="anonym-checkmark">✓</span>
                ) : '-'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Actions */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Aktionen</h2>
        </div>

        <div className="actions-grid">
          {/* Delete/Restore Actions */}
          <div className="action-card">
            <h3>Objektverwaltung</h3>
            <div className="action-buttons-group">
              {isDeleted() ? (
                <button
                  className="action-btn activate"
                  onClick={openRestoreModal}
                  disabled={actionLoading}
                  title="Marktplatzobjekt wiederherstellen"
                >
                  ↩ Wiederherstellen
                </button>
              ) : (
                <button
                  className="action-btn delete"
                  onClick={handleDelete}
                  disabled={actionLoading}
                  title="Marktplatzobjekt löschen"
                >
                  🗑️ Löschen
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Issue #410: Meldungen section (shown only when reports exist) */}
      <MeldungenSection targetType="post" targetId={marketplaceObjectId} />

      {/* Section 6: Bearbeitungslog (Issue #159)
          Marketplace audit entries are classified as target_type='object'
          (distinct from regular post deletions, see Logs view). */}
      <Bearbeitungslog targetType="object" targetId={marketplaceObjectId} />

      {/* Restore Reason Modal */}
      {showRestoreModal && (
        <div className="modal-overlay" onClick={closeRestoreModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Marktplatzobjekt wiederherstellen</h2>
            <p className="modal-description">
              Bitte geben Sie einen Grund für die Wiederherstellung an.
              Dieser wird für Audit-Zwecke gespeichert.
            </p>
            <div className="form-group">
              <label htmlFor="restore-reason">Grund für Wiederherstellung *</label>
              <textarea
                id="restore-reason"
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="Bitte geben Sie einen Grund an..."
                rows={4}
                className="modal-textarea"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={closeRestoreModal}
                disabled={actionLoading}
              >
                Abbrechen
              </button>
              <button
                className="action-btn activate"
                onClick={handleRestore}
                disabled={actionLoading || !restoreReason.trim()}
              >
                {actionLoading ? 'Wird wiederhergestellt...' : 'Wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #408: Delete-with-reason modal */}
      <DeleteReasonModal
        open={showDeleteModal}
        title="Marktplatzobjekt löschen"
        description="Sie sind dabei, dieses Marktplatzobjekt zu löschen. Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

export default MarketplaceDetail;
